import { messages } from '../i18n/messages';
import type { Lang } from '../i18n/types';
import type { QuizTypeKey, SingleQuizTypeKey } from './quizScoring';

export interface ShareCardStat {
  icon: string;
  label: string;
  percent?: number;
  textValue?: string;
}

export interface ZodiacInfo {
  name: string;
  symbol: string;
}

const ZODIAC_TABLE: {
  symbol: string;
  nameVi: string;
  nameEn: string;
  start: [number, number];
  end: [number, number];
}[] = [
  { symbol: '♒', nameVi: 'Bảo Bình', nameEn: 'Aquarius', start: [1, 20], end: [2, 18] },
  { symbol: '♓', nameVi: 'Song Ngư', nameEn: 'Pisces', start: [2, 19], end: [3, 20] },
  { symbol: '♈', nameVi: 'Bạch Dương', nameEn: 'Aries', start: [3, 21], end: [4, 19] },
  { symbol: '♉', nameVi: 'Kim Ngưu', nameEn: 'Taurus', start: [4, 20], end: [5, 20] },
  { symbol: '♊', nameVi: 'Song Tử', nameEn: 'Gemini', start: [5, 21], end: [6, 20] },
  { symbol: '♋', nameVi: 'Cự Giải', nameEn: 'Cancer', start: [6, 21], end: [7, 22] },
  { symbol: '♌', nameVi: 'Sư Tử', nameEn: 'Leo', start: [7, 23], end: [8, 22] },
  { symbol: '♍', nameVi: 'Xử Nữ', nameEn: 'Virgo', start: [8, 23], end: [9, 22] },
  { symbol: '♎', nameVi: 'Thiên Bình', nameEn: 'Libra', start: [9, 23], end: [10, 22] },
  { symbol: '♏', nameVi: 'Bọ Cạp', nameEn: 'Scorpio', start: [10, 23], end: [11, 21] },
  { symbol: '♐', nameVi: 'Nhân Mã', nameEn: 'Sagittarius', start: [11, 22], end: [12, 21] },
  { symbol: '♑', nameVi: 'Ma Kết', nameEn: 'Capricorn', start: [12, 22], end: [1, 19] },
];

function isInRange(month: number, day: number, start: [number, number], end: [number, number]): boolean {
  const [startMonth, startDay] = start;
  const [endMonth, endDay] = end;
  if (startMonth === endMonth) {
    return month === startMonth && day >= startDay && day <= endDay;
  }
  if (startMonth < endMonth) {
    return (
      (month === startMonth && day >= startDay) ||
      (month === endMonth && day <= endDay) ||
      (month > startMonth && month < endMonth)
    );
  }
  return month === startMonth && day >= startDay || month === endMonth && day <= endDay || month > startMonth || month < endMonth;
}

export function getZodiacFromDob(dob: string, lang: Lang = 'vi'): ZodiacInfo | null {
  const parts = dob.split('/');
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  if (!day || !month || month < 1 || month > 12) return null;

  const match = ZODIAC_TABLE.find((z) => isInRange(month, day, z.start, z.end));
  return match
    ? { name: lang === 'en' ? match.nameEn : match.nameVi, symbol: match.symbol }
    : null;
}

const ELEMENT_MAP: Record<string, 'fire' | 'earth' | 'air' | 'water'> = {
  // Vietnamese
  'Bạch Dương': 'fire', 'Sư Tử': 'fire', 'Nhân Mã': 'fire',
  'Kim Ngưu': 'earth', 'Xử Nữ': 'earth', 'Ma Kết': 'earth',
  'Song Tử': 'air', 'Thiên Bình': 'air', 'Bảo Bình': 'air',
  'Cự Giải': 'water', 'Bọ Cạp': 'water', 'Song Ngư': 'water',
  // English
  'Aries': 'fire', 'Leo': 'fire', 'Sagittarius': 'fire',
  'Taurus': 'earth', 'Virgo': 'earth', 'Capricorn': 'earth',
  'Gemini': 'air', 'Libra': 'air', 'Aquarius': 'air',
  'Cancer': 'water', 'Scorpio': 'water', 'Pisces': 'water',
};

export function getZodiacElement(name: string | null | undefined): 'fire' | 'earth' | 'air' | 'water' | null {
  if (!name) return null;
  const trimmed = name.trim();
  return ELEMENT_MAP[trimmed] || null;
}

const TONE_THEMES = {
  hot: {
    gradient: 'linear-gradient(180deg, #0a0a0a 0%, #3d1a1a 55%, #0a0a0a 100%)',
    border: '#ff4500',
    accent: '#ff6347',
    hookColor: '#ffcccb',
    glow: 'radial-gradient(ellipse 90% 55% at 50% 12%, rgba(255,69,0,0.25) 0%, transparent 68%)',
    textPrimary: '#f8fafc',
    textMuted: '#e2e8f0',
    textFooter: '#cbd5e1',
    surfaceBg: 'rgba(0,0,0,0.24)',
    surfaceBorder: 'rgba(255,255,255,0.14)',
  },
  warm: {
    gradient: 'linear-gradient(180deg, #0a0a0a 0%, #2d1f0f 55%, #0a0a0a 100%)',
    border: '#ff8c00',
    accent: '#ffa500',
    hookColor: '#ffe4b5',
    glow: 'radial-gradient(ellipse 90% 55% at 50% 12%, rgba(255,140,0,0.22) 0%, transparent 68%)',
    textPrimary: '#f8fafc',
    textMuted: '#e2e8f0',
    textFooter: '#cbd5e1',
    surfaceBg: 'rgba(0,0,0,0.24)',
    surfaceBorder: 'rgba(255,255,255,0.14)',
  },
  cool: {
    gradient: 'linear-gradient(180deg, #0a0a0a 0%, #1a2a4a 55%, #0a0a0a 100%)',
    border: '#4682b4',
    accent: '#87ceeb',
    hookColor: '#e0ffff',
    glow: 'radial-gradient(ellipse 90% 55% at 50% 12%, rgba(70,130,180,0.22) 0%, transparent 68%)',
    textPrimary: '#f8fafc',
    textMuted: '#e2e8f0',
    textFooter: '#cbd5e1',
    surfaceBg: 'rgba(0,0,0,0.24)',
    surfaceBorder: 'rgba(255,255,255,0.14)',
  },
  icy: {
    gradient: 'linear-gradient(180deg, #0a0a0a 0%, #0c1929 55%, #0a0a0a 100%)',
    border: '#67e8f9',
    accent: '#7dd3fc',
    hookColor: '#cffafe',
    glow: 'radial-gradient(ellipse 90% 55% at 50% 12%, rgba(103,232,249,0.22) 0%, transparent 68%)',
    textPrimary: '#f8fafc',
    textMuted: '#e2e8f0',
    textFooter: '#cbd5e1',
    surfaceBg: 'rgba(0,0,0,0.24)',
    surfaceBorder: 'rgba(255,255,255,0.14)',
  },
} as const;

export function getToneTheme(heat: number) {
  if (heat > 0.75) return TONE_THEMES.hot;
  if (heat > 0.5) return TONE_THEMES.warm;
  if (heat > 0.25) return TONE_THEMES.cool;
  return TONE_THEMES.icy;
}

export const QUIZ_SHARE_NAME_STORAGE_KEY = 'nagiQuizShareDisplayName';

/** Tên gọi ở nhà / nickname — không dùng họ tên đầy đủ trên Story */
export function deriveDefaultShareName(nickname: unknown, fullName: unknown): string {
  const nick = typeof nickname === 'string' ? nickname : '';
  const full = typeof fullName === 'string' ? fullName : '';
  if (nick.trim()) return nick.trim();
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return parts[parts.length - 1];
}

export function readStoredShareDisplayName(): string {
  try {
    return sessionStorage.getItem(QUIZ_SHARE_NAME_STORAGE_KEY)?.trim() || '';
  } catch {
    return '';
  }
}

export function writeStoredShareDisplayName(name: string): void {
  try {
    const trimmed = name.trim();
    if (trimmed) {
      sessionStorage.setItem(QUIZ_SHARE_NAME_STORAGE_KEY, trimmed);
    } else {
      sessionStorage.removeItem(QUIZ_SHARE_NAME_STORAGE_KEY);
    }
  } catch {
    // sessionStorage có thể bị chặn ở chế độ ẩn danh
  }
}

/** Ưu tiên tên người dùng tự nhập, rồi sessionStorage, rồi nickname/họ tên */
export function resolveInitialShareDisplayName(nickname: unknown, fullName: unknown): string {
  const stored = readStoredShareDisplayName();
  if (stored) return stored;
  return deriveDefaultShareName(nickname, fullName);
}

export interface ShareProfileMeta {
  city?: string;
  zodiac?: string;
  zodiacSymbol?: string;
}

/** Tách tỉnh/TP và cung hoàng đạo cho layout avatar trái — info phải */
export function buildShareProfileMeta(city: string, dob: string, lang: Lang = 'vi'): ShareProfileMeta {
  const trimmedCity = city.trim();
  const zodiacInfo = getZodiacFromDob(dob, lang);
  const prefix = messages[lang].share.card.zodiacPrefix;
  const zodiacLabel = zodiacInfo
    ? prefix
      ? `${prefix} ${zodiacInfo.name}`
      : zodiacInfo.name
    : undefined;
  return {
    city: trimmedCity || undefined,
    zodiac: zodiacLabel,
    zodiacSymbol: zodiacInfo?.symbol,
  };
}

/** Chỉ tỉnh/TP + cung hoàng đạo — không hiển thị tuổi */
export function buildShareSubtitle(city: string, dob: string, lang: Lang = 'vi'): string | undefined {
  const { city: cityLabel, zodiac } = buildShareProfileMeta(city, dob, lang);
  const segments = [cityLabel, zodiac].filter(Boolean);
  return segments.length > 0 ? segments.join(' · ') : undefined;
}

export const SHARE_CARD_FONT = "'Be Vietnam Pro', 'Inter', system-ui, sans-serif";

export type ShareCardAspect = 'story' | 'feed';

export interface ShareCardSpec {
  aspect: ShareCardAspect;
  width: number;
  height: number;
  previewWidth: number;
  previewHeight: number;
  ratioLabel: string;
  platformLabel: string;
  exportLabel: string;
  filenameSuffix: string;
}

export const SHARE_CARD_SPECS: Record<ShareCardAspect, ShareCardSpec> = {
  story: {
    aspect: 'story',
    width: 1080,
    height: 1920,
    previewWidth: 270,
    previewHeight: 480,
    ratioLabel: '9:16',
    platformLabel: 'Story / Reels',
    exportLabel: '1080×1920',
    filenameSuffix: 'story',
  },
  feed: {
    aspect: 'feed',
    width: 1080,
    height: 1440,
    previewWidth: 270,
    previewHeight: 360,
    ratioLabel: '3:4',
    platformLabel: 'Facebook / Instagram / Threads',
    exportLabel: '1080×1440',
    filenameSuffix: 'feed',
  },
};

export function getShareCardSpec(aspect: ShareCardAspect = 'story'): ShareCardSpec {
  return SHARE_CARD_SPECS[aspect];
}

/** Kích thước mặc định — Story 9:16 */
export const SHARE_CARD_WIDTH = SHARE_CARD_SPECS.story.width;
export const SHARE_CARD_HEIGHT = SHARE_CARD_SPECS.story.height;
export const SHARE_CARD_PREVIEW_WIDTH = 270;
export const SHARE_CARD_PREVIEW_HEIGHT = SHARE_CARD_SPECS.story.previewHeight;

export type ShareCardAvatarMode = 'personal' | 'default';

export const QUIZ_SHARE_AVATAR_MODE_STORAGE_KEY = 'nagiQuizShareAvatarMode';

export function readStoredShareAvatarMode(): ShareCardAvatarMode {
  try {
    return sessionStorage.getItem(QUIZ_SHARE_AVATAR_MODE_STORAGE_KEY) === 'default'
      ? 'default'
      : 'personal';
  } catch {
    return 'personal';
  }
}

export function writeStoredShareAvatarMode(mode: ShareCardAvatarMode): void {
  try {
    sessionStorage.setItem(QUIZ_SHARE_AVATAR_MODE_STORAGE_KEY, mode);
  } catch {
    // sessionStorage có thể bị chặn
  }
}

export function resolveShareCardAvatarUrl(
  mode: ShareCardAvatarMode,
  avatarUrls: string[],
  canChoosePersonalAvatar: boolean
): string | undefined {
  if (!canChoosePersonalAvatar || mode === 'default') return undefined;
  return avatarUrls[0] || undefined;
}

export const QUIZ_SHARE_ASPECT_STORAGE_KEY = 'nagiQuizShareAspect';

export function readStoredShareAspect(): ShareCardAspect {
  try {
    return sessionStorage.getItem(QUIZ_SHARE_ASPECT_STORAGE_KEY) === 'feed' ? 'feed' : 'story';
  } catch {
    return 'story';
  }
}

export function writeStoredShareAspect(aspect: ShareCardAspect): void {
  try {
    sessionStorage.setItem(QUIZ_SHARE_ASPECT_STORAGE_KEY, aspect);
  } catch {
    // sessionStorage có thể bị chặn
  }
}

export interface ShareCardMetrics {
  spec: ShareCardSpec;
  px: (value: number) => number;
  isFeed: boolean;
  hookLineClamp: number;
}

/** Scale px từ bản thiết kế 270px lên kích thước xuất (mặc định 1080px) */
export function cardPx(value: number, exportWidth = SHARE_CARD_SPECS.story.width): number {
  const scale = exportWidth / SHARE_CARD_PREVIEW_WIDTH;
  return Math.round(value * scale);
}

/** Metrics theo tỉ lệ — feed 3:4 dùng spacing gọn hơn */
export function createShareCardMetrics(aspect: ShareCardAspect = 'story'): ShareCardMetrics {
  const spec = getShareCardSpec(aspect);
  const isFeed = aspect === 'feed';
  const compact = isFeed ? 0.84 : 1;
  return {
    spec,
    isFeed,
    hookLineClamp: isFeed ? 3 : 4,
    px: (value: number) => cardPx(value * compact, spec.width),
  };
}

export interface ShareCardTheme {
  border: string;
  accent: string;
  hookColor: string;
  gradient: string;
  glow?: string;
  textPrimary: string;
  textMuted: string;
  textFooter: string;
  surfaceBg: string;
  surfaceBorder: string;
}

/** @deprecated alias */
export type FrozenHeartCardTheme = ShareCardTheme;

type ShareCardThemeInput = Pick<ShareCardTheme, 'border' | 'accent' | 'hookColor' | 'gradient'> &
  Partial<Pick<ShareCardTheme, 'glow' | 'textPrimary' | 'textMuted' | 'textFooter' | 'surfaceBg' | 'surfaceBorder'>>;

function cardTheme(input: ShareCardThemeInput): ShareCardTheme {
  return {
    textPrimary: '#f8fafc',
    textMuted: '#e2e8f0',
    textFooter: '#cbd5e1',
    surfaceBg: 'rgba(0,0,0,0.24)',
    surfaceBorder: 'rgba(255,255,255,0.14)',
    ...input,
  };
}

const PERSONALITY_DEFAULT_THEME = cardTheme({
  border: '#d4af37',
  accent: '#f5d76e',
  hookColor: '#fef3c7',
  gradient: 'linear-gradient(155deg, #120e1a 0%, #3d2a4a 32%, #221830 58%, #0e0a14 100%)',
  glow: 'radial-gradient(ellipse 90% 55% at 50% 12%, rgba(212,175,55,0.22) 0%, transparent 68%)',
});

const PERSONALITY_VARIANT_THEMES: Record<string, ShareCardTheme> = {
  A1: cardTheme({
    border: '#e8c547',
    accent: '#fde68a',
    hookColor: '#fff7d6',
    gradient: 'linear-gradient(155deg, #1a1206 0%, #5c4510 30%, #3d2e0c 55%, #140f04 100%)',
    glow: 'radial-gradient(ellipse 85% 50% at 48% 15%, rgba(234,197,71,0.28) 0%, transparent 70%)',
  }),
  A2: cardTheme({
    border: '#818cf8',
    accent: '#a5b4fc',
    hookColor: '#e0e7ff',
    gradient: 'linear-gradient(155deg, #0c0820 0%, #312e81 34%, #1e1b4b 58%, #08061a 100%)',
    glow: 'radial-gradient(ellipse 80% 48% at 52% 18%, rgba(129,140,248,0.3) 0%, transparent 72%)',
  }),
  A3: cardTheme({
    border: '#94a3b8',
    accent: '#cbd5e1',
    hookColor: '#f1f5f9',
    gradient: 'linear-gradient(155deg, #0a1018 0%, #1e3a5f 32%, #172554 56%, #060a12 100%)',
    glow: 'radial-gradient(ellipse 82% 50% at 50% 16%, rgba(56,189,248,0.2) 0%, transparent 70%)',
  }),
  B1: cardTheme({
    border: '#fb7185',
    accent: '#fda4af',
    hookColor: '#ffe4e6',
    gradient: 'linear-gradient(155deg, #1a0510 0%, #831843 34%, #500724 58%, #120208 100%)',
    glow: 'radial-gradient(ellipse 85% 52% at 50% 14%, rgba(251,113,133,0.32) 0%, transparent 70%)',
  }),
  B2: cardTheme({
    border: '#fbbf24',
    accent: '#fcd34d',
    hookColor: '#fef9c3',
    gradient: 'linear-gradient(155deg, #1a0e04 0%, #92400e 32%, #78350f 56%, #120a02 100%)',
    glow: 'radial-gradient(ellipse 88% 54% at 48% 16%, rgba(251,191,36,0.3) 0%, transparent 72%)',
  }),
  B3: cardTheme({
    border: '#f43f5e',
    accent: '#fb7185',
    hookColor: '#fecdd3',
    gradient: 'linear-gradient(155deg, #180208 0%, #9f1239 36%, #6b0728 60%, #0f0106 100%)',
    glow: 'radial-gradient(ellipse 86% 50% at 52% 15%, rgba(244,63,94,0.34) 0%, transparent 70%)',
  }),
  C1: cardTheme({
    border: '#f59e0b',
    accent: '#fbbf24',
    hookColor: '#fef3c7',
    gradient: 'linear-gradient(155deg, #1a1006 0%, #7c4a12 30%, #5c3710 54%, #100a04 100%)',
    glow: 'radial-gradient(ellipse 84% 52% at 50% 17%, rgba(245,158,11,0.28) 0%, transparent 70%)',
  }),
  C2: cardTheme({
    border: '#fda4af',
    accent: '#fecdd3',
    hookColor: '#fff1f2',
    gradient: 'linear-gradient(155deg, #1a0810 0%, #9d174d 30%, #6b2140 54%, #100408 100%)',
    glow: 'radial-gradient(ellipse 82% 50% at 50% 16%, rgba(253,164,175,0.3) 0%, transparent 72%)',
  }),
  C3: cardTheme({
    border: '#4ade80',
    accent: '#86efac',
    hookColor: '#dcfce7',
    gradient: 'linear-gradient(155deg, #04140a 0%, #166534 32%, #14532d 56%, #020a06 100%)',
    glow: 'radial-gradient(ellipse 84% 52% at 50% 15%, rgba(74,222,128,0.26) 0%, transparent 70%)',
  }),
  D1: cardTheme({
    border: '#f5d76e',
    accent: '#fde047',
    hookColor: '#fef9c3',
    gradient: 'linear-gradient(155deg, #14101a 0%, #4c3d6e 30%, #2e2548 55%, #0a0812 100%)',
    glow: 'radial-gradient(ellipse 86% 52% at 50% 14%, rgba(245,215,110,0.26) 0%, transparent 70%)',
  }),
  D2: cardTheme({
    border: '#e879f9',
    accent: '#f0abfc',
    hookColor: '#fae8ff',
    gradient: 'linear-gradient(155deg, #140818 0%, #86198f 34%, #581c87 58%, #0a040e 100%)',
    glow: 'radial-gradient(ellipse 88% 54% at 50% 16%, rgba(232,121,249,0.32) 0%, transparent 72%)',
  }),
  D3: cardTheme({
    border: '#a3e635',
    accent: '#bef264',
    hookColor: '#ecfccb',
    gradient: 'linear-gradient(155deg, #0c1404 0%, #3f6212 32%, #365314 56%, #060a02 100%)',
    glow: 'radial-gradient(ellipse 84% 50% at 50% 15%, rgba(163,230,53,0.28) 0%, transparent 70%)',
  }),
};

export function getPersonalityCardTheme(variantKey?: string): ShareCardTheme {
  if (variantKey && PERSONALITY_VARIANT_THEMES[variantKey]) {
    return PERSONALITY_VARIANT_THEMES[variantKey];
  }
  return PERSONALITY_DEFAULT_THEME;
}

const PERSONALITY_STATS: Record<Lang, Record<QuizTypeKey, ShareCardStat[]>> = {
  vi: {
    A: [
      { icon: '🔇', label: 'Mức kín tiếng', percent: 92 },
      { icon: '🧠', label: 'Chiều sâu tâm hồn', percent: 88 },
      { icon: '📦', label: 'Cấu hình thể chất', textValue: 'Stealth Edition' },
    ],
    B: [
      { icon: '🔥', label: 'Độ cháy hết mình', percent: 95 },
      { icon: '📱', label: 'Phủ sóng story', percent: 90 },
      { icon: '📦', label: 'Cấu hình thể chất', textValue: 'Premium Edition' },
    ],
    C: [
      { icon: '🏠', label: 'Chỉ số che chở', percent: 93 },
      { icon: '🍲', label: 'Skill nấu ăn', percent: 87 },
      { icon: '📦', label: 'Cấu hình thể chất', textValue: 'Comfort Edition' },
    ],
    D: [
      { icon: '😂', label: 'Độ mặn mòi', percent: 96 },
      { icon: '🎉', label: 'Năng lượng vibe', percent: 91 },
      { icon: '📦', label: 'Cấu hình thể chất', textValue: 'Fun Edition' },
    ],
  },
  en: {
    A: [
      { icon: '🔇', label: 'Quiet level', percent: 92 },
      { icon: '🧠', label: 'Soul depth', percent: 88 },
      { icon: '📦', label: 'Physical config', textValue: 'Stealth Edition' },
    ],
    B: [
      { icon: '🔥', label: 'All-in intensity', percent: 95 },
      { icon: '📱', label: 'Story coverage', percent: 90 },
      { icon: '📦', label: 'Physical config', textValue: 'Premium Edition' },
    ],
    C: [
      { icon: '🏠', label: 'Protectiveness', percent: 93 },
      { icon: '🍲', label: 'Cooking skill', percent: 87 },
      { icon: '📦', label: 'Physical config', textValue: 'Comfort Edition' },
    ],
    D: [
      { icon: '😂', label: 'Saltiness level', percent: 96 },
      { icon: '🎉', label: 'Vibe energy', percent: 91 },
      { icon: '📦', label: 'Physical config', textValue: 'Fun Edition' },
    ],
  },
};

const FROZEN_HEART_DEFAULT_THEME = cardTheme({
  border: '#67e8f9',
  accent: '#7dd3fc',
  hookColor: '#cffafe',
  gradient: 'linear-gradient(155deg, #041018 0%, #0c4a6e 34%, #082f49 58%, #020810 100%)',
  glow: 'radial-gradient(ellipse 88% 52% at 50% 14%, rgba(103,232,249,0.28) 0%, transparent 72%)',
});

const FROZEN_HEART_VARIANT_THEMES: Record<string, ShareCardTheme> = {
  A1: cardTheme({
    border: '#e8c547',
    accent: '#fde68a',
    hookColor: '#fff7d6',
    gradient: 'linear-gradient(155deg, #0e0c06 0%, #4a3d14 30%, #2e280c 54%, #0a0804 100%)',
    glow: 'radial-gradient(ellipse 84% 50% at 50% 16%, rgba(232,197,71,0.26) 0%, transparent 70%)',
  }),
  A2: cardTheme({
    border: '#c084fc',
    accent: '#d8b4fe',
    hookColor: '#f3e8ff',
    gradient: 'linear-gradient(155deg, #100818 0%, #6b21a8 34%, #4c1d95 58%, #08040e 100%)',
    glow: 'radial-gradient(ellipse 86% 52% at 50% 15%, rgba(192,132,252,0.3) 0%, transparent 72%)',
  }),
  A3: cardTheme({
    border: '#38bdf8',
    accent: '#7dd3fc',
    hookColor: '#e0f2fe',
    gradient: 'linear-gradient(155deg, #041018 0%, #0369a1 32%, #0c4a6e 56%, #020810 100%)',
    glow: 'radial-gradient(ellipse 85% 50% at 50% 14%, rgba(56,189,248,0.3) 0%, transparent 70%)',
  }),
  B1: cardTheme({
    border: '#f9a8d4',
    accent: '#f472b6',
    hookColor: '#fce7f3',
    gradient: 'linear-gradient(155deg, #180810 0%, #9d174d 32%, #701a40 56%, #0c0408 100%)',
    glow: 'radial-gradient(ellipse 84% 52% at 50% 16%, rgba(244,114,182,0.3) 0%, transparent 72%)',
  }),
  B2: cardTheme({
    border: '#94a3b8',
    accent: '#cbd5e1',
    hookColor: '#f1f5f9',
    gradient: 'linear-gradient(155deg, #0a0e14 0%, #334155 34%, #1e293b 58%, #06080c 100%)',
    glow: 'radial-gradient(ellipse 82% 48% at 50% 15%, rgba(148,163,184,0.24) 0%, transparent 70%)',
  }),
  B3: cardTheme({
    border: '#fcd34d',
    accent: '#fde68a',
    hookColor: '#fef9c3',
    gradient: 'linear-gradient(155deg, #141006 0%, #854d0e 32%, #5c3a0a 56%, #0c0802 100%)',
    glow: 'radial-gradient(ellipse 86% 52% at 50% 16%, rgba(252,211,77,0.28) 0%, transparent 70%)',
  }),
  C1: cardTheme({
    border: '#22d3ee',
    accent: '#67e8f9',
    hookColor: '#cffafe',
    gradient: 'linear-gradient(155deg, #021018 0%, #0e7490 34%, #155e75 58%, #010810 100%)',
    glow: 'radial-gradient(ellipse 88% 54% at 50% 14%, rgba(34,211,238,0.32) 0%, transparent 72%)',
  }),
  C2: cardTheme({
    border: '#fb923c',
    accent: '#fdba74',
    hookColor: '#ffedd5',
    gradient: 'linear-gradient(155deg, #180a04 0%, #c2410c 32%, #9a3412 56%, #0c0602 100%)',
    glow: 'radial-gradient(ellipse 84% 50% at 50% 16%, rgba(251,146,60,0.3) 0%, transparent 70%)',
  }),
  C3: cardTheme({
    border: '#4ade80',
    accent: '#86efac',
    hookColor: '#dcfce7',
    gradient: 'linear-gradient(155deg, #04140a 0%, #15803d 32%, #166534 56%, #020a06 100%)',
    glow: 'radial-gradient(ellipse 84% 52% at 50% 15%, rgba(74,222,128,0.28) 0%, transparent 70%)',
  }),
};

export function getFrozenHeartCardTheme(variantKey?: string): ShareCardTheme {
  if (variantKey && FROZEN_HEART_VARIANT_THEMES[variantKey]) {
    return FROZEN_HEART_VARIANT_THEMES[variantKey];
  }
  return FROZEN_HEART_DEFAULT_THEME;
}

const FROZEN_HEART_STATS: Record<Lang, Record<SingleQuizTypeKey, ShareCardStat[]>> = {
  vi: {
    A: [
      { icon: '👑', label: 'Gu kén chọn', percent: 95 },
      { icon: '🥂', label: 'Tiêu chuẩn hoàng gia', percent: 90 },
      { icon: '📦', label: 'Cấu hình thể chất', textValue: 'VIP Edition' },
    ],
    B: [
      { icon: '🛡️', label: 'Lớp băng tự vệ', percent: 88 },
      { icon: '💔', label: 'Điểm tổn thương', percent: 75 },
      { icon: '📦', label: 'Cấu hình thể chất', textValue: 'Shield Edition' },
    ],
    C: [
      { icon: '🧊', label: 'Độ lười ra đường', percent: 85 },
      { icon: '🔋', label: 'Dung lượng pin giường chiếu', percent: 35 },
      { icon: '📦', label: 'Cấu hình thể chất', textValue: 'Standard Edition' },
    ],
  },
  en: {
    A: [
      { icon: '👑', label: 'Picky standards', percent: 95 },
      { icon: '🥂', label: 'Royal standards', percent: 90 },
      { icon: '📦', label: 'Physical config', textValue: 'VIP Edition' },
    ],
    B: [
      { icon: '🛡️', label: 'Ice shield', percent: 88 },
      { icon: '💔', label: 'Wound score', percent: 75 },
      { icon: '📦', label: 'Physical config', textValue: 'Shield Edition' },
    ],
    C: [
      { icon: '🧊', label: 'Stay-home level', percent: 85 },
      { icon: '🔋', label: 'Bed battery', percent: 35 },
      { icon: '📦', label: 'Physical config', textValue: 'Standard Edition' },
    ],
  },
};

export function getShareCardStats(
  variant: 'personality' | 'single' | 'bedroom',
  typeKey: string,
  lang: Lang = 'vi'
): ShareCardStat[] {
  if (variant === 'personality' && (typeKey === 'A' || typeKey === 'B' || typeKey === 'C' || typeKey === 'D')) {
    return PERSONALITY_STATS[lang][typeKey];
  }
  if (variant === 'single' && (typeKey === 'A' || typeKey === 'B' || typeKey === 'C')) {
    return FROZEN_HEART_STATS[lang][typeKey];
  }
  if (variant === 'bedroom') {
    // Fixed 3 lines per group for bedroom, using textValue
    const bars: Record<string, ShareCardStat[]> = {
      A: [
        { icon: '🧸', label: 'Chế độ vận hành', textValue: 'Eco Mode' },
        { icon: '⚡', label: 'Xung nhịp phần cứng', textValue: 'Elegant Design' },
        { icon: '🔋', label: 'Dung lượng Pin', textValue: 'Tiêu chuẩn' },
      ],
      B: [
        { icon: '⚙️', label: 'Chế độ vận hành', textValue: 'Lật Kèo Phút Chót' },
        { icon: '⚡', label: 'Xung nhịp phần cứng', textValue: 'Stealth Edition' },
        { icon: '🔋', label: 'Dung lượng Pin', textValue: 'Sạc Nhồi 2 Hiệp' },
      ],
      C: [
        { icon: '🔥', label: 'Chế độ vận hành', textValue: 'Sport Plus Mode' },
        { icon: '⚡', label: 'Xung nhịp phần cứng', textValue: 'Ultra Overclocked' },
        { icon: '🔋', label: 'Dung lượng Pin', textValue: 'Pin Trâu 5000mAh' },
      ],
      D: [
        { icon: '😂', label: 'Chế độ vận hành', textValue: 'Vừa Làm Vừa Diễn Hài' },
        { icon: '⚡', label: 'Xung nhịp phần cứng', textValue: 'Compact Edition' },
        { icon: '🔋', label: 'Dung lượng Pin', textValue: 'Fast Charge 15 Phút' },
      ],
    };
    return bars[typeKey] || [];
  }
  return [];
}